import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import React, { useMemo } from 'react';

import { KioskType } from '../../../context/types';
import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import type { DeviceProps } from '../types';
import styles from './button.module.scss';

export type ButtonProps = DeviceProps & {
  children: string | React.ReactNode;
  color?: 'primary' | 'secondary';
  backgroundColor?: 'primary' | string;
  variant?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  flexWide?: boolean;
  kioskSmall?: boolean;
  capitalize?: boolean;
  uppercase?: boolean;
  ariaLabel?: string;
};

const ButtonStyled: React.FC<ButtonProps> = ({
  children,
  disabled,
  onClick,
  className = '',
  flexWide = false,
  color = 'primary',
  variant = 'contained',
  backgroundColor = 'primary',
  kioskSmall = false,
  capitalize,
  uppercase,
  ariaLabel,
}) => {
  const { kiosk, kioskMode } = useKioskManager();
  const isMobile = useIsMobile();

  const classes = {
    root: `${styles.buttonClass} ${kiosk && kioskMode && styles[kiosk.model]} ${
      flexWide && styles.flexWide
    } ${className} `,
  };

  const calculatedHeight = useMemo(() => {
    if (kioskMode && kiosk) {
      if (kiosk.model === KioskType.ALL_MIGHTY) {
        return 'unset';
      }
      if (kioskSmall) {
        return '43px';
      }
      return '60px';
    }
    return !isMobile ? '43px' : '';
  }, [kioskMode, kiosk, isMobile, kioskSmall]);

  const BaseButton = styled(Button)({
    borderRadius: variant === 'text' ? '16px' : '24px',
    padding: variant === 'text' ? '4px' : '12px 24px',
    height: calculatedHeight,
    fontSize: '16px',
    textTransform: uppercase ? 'uppercase' : capitalize ? 'capitalize' : 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: backgroundColor,
    ariaLabel: ariaLabel,
  });

  return (
    <BaseButton
      classes={classes}
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      color={color}
      aria-label={ariaLabel}
      role="button"
    >
      {children}
    </BaseButton>
  );
};

export default ButtonStyled;
